import { useEffect, useState } from "react";
import { Combobox, type ComboboxOption } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getProject, getProjects } from "../../../../lib/projects";

/**
 * The size of a CRM form field, worn by the Combobox trigger. Copied from
 * `ClientPicker` on purpose: these two stand side by side on every form that
 * has both, and a project field one pixel off the client field beside it is the
 * kind of difference nobody can name and everybody sees.
 */
const PILL = "h-[52px] border-0 px-[18px] text-[13.5px] shadow-none";

/**
 * How many of the client's projects the list offers. A client with more than a
 * hundred live projects does not exist in this CRM, and if one ever does the
 * search box in the panel is the way through - the cap is on what is fetched,
 * not on what can be reached, because a project outside the page is still
 * resolved by id below.
 */
const PAGE = 100;

/**
 * Picks the project a document or an expense belongs to, SCOPED to one client.
 *
 * Scoping is the whole point rather than a nicety. `Estimate.projectId`,
 * `Invoice.projectId` and `Expense.projectId` are plain string columns with no
 * foreign key behind them (apps/api/prisma/crm/schema.prisma), so the database
 * will happily store a project belonging to somebody else. The sales routes
 * refuse that with `400 project_client_mismatch`; the expense routes do not
 * check at all. An unscoped list of every project in the CRM would therefore be
 * a field that produces a rejected save on the good path and one client's money
 * counted under another client's name on the bad one.
 *
 * Three behaviours that are not obvious from the props:
 *
 *  - With no client chosen the field is disabled and SAYS why. A project always
 *    belongs to a client, so "any project" is not a state this field can be in.
 *  - Clearing the value is deliberate (`clearable`): a document can stop
 *    belonging to a project, and the alternative is a link the operator can set
 *    once and never take back.
 *  - A value the client's page does not contain is looked up by id and added to
 *    the list, so the trigger shows the project's NAME instead of falling back
 *    to the placeholder. A picker that renders empty while holding a saved id
 *    reads as "no project", and the next save would write that lie back.
 *
 * Clearing an invalidated value when the CLIENT changes is deliberately NOT
 * done here: this component never learns whether `clientId` changed because the
 * operator picked a different client (the old project is now wrong) or because
 * the form finished loading (the saved project is right). The form owns that
 * decision, which is one line in its client `onChange`.
 */
export function ProjectPicker({
  clientId,
  value,
  onChange,
  disabled,
}: {
  /** The client the document or expense belongs to. Empty = none chosen yet. */
  clientId: string;
  /** The linked project's id, or "" for none. */
  value: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  // Flips once the client's own projects have arrived, so the lookup below can
  // tell "this client has no such project" from "the list has not loaded yet"
  // and does not fire a second request against an empty array every time.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    if (!clientId) {
      setOptions([]);
      return () => {
        alive = false;
      };
    }
    getProjects({ clientId, pageSize: PAGE })
      .then((res) => {
        if (!alive) return;
        // By name, not by the route's `createdAt desc`: this is a list somebody
        // reads looking for a name they already know.
        setOptions(
          res.rows
            .map((p) => ({ value: p.id, label: p.name }))
            .sort((a, b) => a.label.localeCompare(b.label, "et")),
        );
      })
      .catch(() => {
        if (alive) setOptions([]);
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (!loaded || !value || options.some((o) => o.value === value)) return;
    let alive = true;
    getProject(value)
      .then((p) => {
        if (!alive) return;
        setOptions((prev) =>
          prev.some((o) => o.value === p.id)
            ? prev
            : // The client's name as the hint when the project turns out to
              // belong to somebody else: that row is a mismatch waiting to be
              // rejected, and the operator has to be able to see it rather than
              // find out at the save.
              [{ value: p.id, label: p.name, hint: p.clientId === clientId ? undefined : p.client?.name }, ...prev],
        );
      })
      .catch(() => {
        // A 404 means the project is gone. Leaving the trigger on its
        // placeholder is then the truth, so there is nothing to do here.
      });
    return () => {
      alive = false;
    };
  }, [clientId, loaded, options, value]);

  const noClient = !clientId;

  return (
    <Combobox
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled || noClient}
      clearable
      className={PILL}
      popoverClassName="rounded-2xl"
      placeholder={noClient ? t("sales.projectNeedsClient") : t("sales.projectNone")}
      searchPlaceholder={t("sales.searchProject")}
      emptyLabel={t("sales.noProjects")}
    />
  );
}
